// Te controleren (B31, AI2): what the AI was not sure about waits here until PK says Klopt, Aanpassen or Weg.
// And the bar after the AI put something in place, with "Ongedaan maken".
import { useEffect, useRef, useState } from "react";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";
import { applicable, describeUnderstood, type Understood } from "@shared/ai";
import { displayDate } from "@shared/time";
import { acceptProposal, aiTidy, dismissProposal, undoApplied, useAiEnabled, useToReview } from "../lib/ai";
import { ApiError } from "../lib/api";
import { db, getMeta, setMeta, type Task } from "../lib/db";
import { useLiveQuery } from "dexie-react-hooks";
import { useCategories } from "../lib/categories";
import { Sheet } from "./Sheet";
import { Button, cn } from "./ui";

export function ToReview() {
  const items = useToReview();
  const [editing, setEditing] = useState<Task | null>(null);
  if (!items?.length) return null;
  return (
    <section aria-labelledby="te-controleren" className="mb-6 rounded-2xl border-2 border-sun bg-surface p-3">
      <h2 id="te-controleren" className="mb-1 flex items-center gap-2 text-lg font-semibold">
        <Sparkles aria-hidden className="size-5 text-pine" />
        Te controleren
      </h2>
      <p className="mb-3 text-sm text-ink-soft">Hier twijfelde de app. Klopt het, dan zet hij het op zijn plek.</p>
      <ul className="flex flex-col gap-3">
        {items.map((t) => {
          const p = t.ai_proposal!;
          const ready = applicable(p);
          return (
            <li key={t.id} className="rounded-xl bg-paper p-3">
              <p className="text-sm text-ink-soft [overflow-wrap:anywhere]">"{t.title}"</p>
              <p className="font-semibold [overflow-wrap:anywhere]">{describeUnderstood(p, displayDate)}</p>
              {p.doubt && <p className="text-sm text-ink-soft">{p.doubt}</p>}
              <div className="mt-2 grid grid-cols-3 gap-2">
                <Button
                  disabled={!ready}
                  onClick={async () => {
                    await acceptProposal(t, p);
                    toast("Op zijn plek gezet");
                  }}
                >
                  Klopt
                </Button>
                <Button variant="secondary" onClick={() => setEditing(t)}>
                  Aanpassen
                </Button>
                <Button variant="secondary" onClick={() => void dismissProposal(t)}>
                  Weg
                </Button>
              </div>
            </li>
          );
        })}
      </ul>
      {editing && <EditProposal task={editing} onClose={() => setEditing(null)} />}
    </section>
  );
}

const KINDS = [
  ["appointment", "Afspraak"],
  ["reminder", "Herinnering"],
  ["task", "Taak"],
  ["date", "Elk jaar"],
] as const;

function EditProposal({ task, onClose }: { task: Task; onClose: () => void }) {
  const categories = useCategories();
  const [p, setP] = useState<Understood>(task.ai_proposal!);
  const set = <K extends keyof Understood>(key: K, value: Understood[K]) =>
    setP((old) => ({ ...old, [key]: value, ...((key === "category" && value) || (key === "date" && value) ? { someday: false } : {}) }));
  const ready = applicable(p) && p.title.trim().length > 0;
  return (
    <Sheet open onOpenChange={(o) => !o && onClose()} title="Aanpassen" description={`"${task.title}"`}>
      <div className="flex flex-col gap-4">
        <div role="radiogroup" aria-label="Wat is het" className="grid grid-cols-2 gap-2">
          {KINDS.map(([value, label]) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={p.kind === value}
              onClick={() => set("kind", value)}
              className={cn(
                "min-h-12 rounded-xl border-2 font-semibold",
                p.kind === value ? "border-pine bg-pine text-paper" : "border-mist bg-paper text-ink",
              )}
            >
              {label}
            </button>
          ))}
        </div>
        <label className="flex flex-col gap-1">
          <span className="font-semibold">Titel</span>
          <input
            value={p.title}
            maxLength={500}
            onChange={(e) => set("title", e.target.value)}
            className="min-h-12 rounded-xl border-2 border-mist bg-paper px-3 text-ink"
          />
        </label>
        <div className="flex flex-wrap gap-3">
          <label className="flex flex-col gap-1">
            <span className="font-semibold">Dag</span>
            <input
              type="date"
              value={p.date ?? ""}
              onChange={(e) => set("date", e.target.value || null)}
              className="min-h-12 rounded-xl border-2 border-mist bg-paper px-3 text-ink"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="font-semibold">Tijd</span>
            <input
              type="time"
              value={p.time ?? ""}
              onChange={(e) => set("time", e.target.value || null)}
              className="min-h-12 rounded-xl border-2 border-mist bg-paper px-3 text-ink"
            />
          </label>
        </div>
        {p.kind === "task" && categories && categories.length > 0 && (
          <div role="radiogroup" aria-label="Categorie" className="flex flex-wrap gap-2">
            {[{ id: "geen", name: null as string | null }, ...categories.map((c) => ({ id: c.id, name: c.name as string | null }))].map(
              (c) => (
                <button
                  key={c.id}
                  type="button"
                  role="radio"
                  aria-checked={p.category === c.name}
                  onClick={() => set("category", c.name)}
                  className={cn(
                    "min-h-11 rounded-xl border-2 px-3",
                    p.category === c.name ? "border-pine bg-pine text-paper" : "border-mist bg-paper text-ink",
                  )}
                >
                  {c.name ?? "Geen"}
                </button>
              ),
            )}
          </div>
        )}
        {!applicable(p) && <p className="text-sm text-ink-soft">Een afspraak en een herinnering hebben een dag en een tijd nodig.</p>}
        <Button
          disabled={!ready}
          onClick={async () => {
            await acceptProposal(task, { ...p, title: p.title.trim(), confidence: 1, doubt: null });
            toast("Op zijn plek gezet");
            onClose();
          }}
        >
          Opslaan
        </Button>
      </div>
    </Sheet>
  );
}

/** Nieuw opruimen (AI golf B, golf 10): one button; everything gets a proposal in Te controleren. */
export function TidyButton({ count }: { count: number }) {
  const ai = useAiEnabled();
  const [busy, setBusy] = useState(false);
  if (!ai || count < 2) return null;
  return (
    <Button
      variant="secondary"
      className="mb-4 w-full"
      disabled={busy}
      onClick={async () => {
        setBusy(true);
        try {
          const { proposals } = await aiTidy();
          toast(proposals ? `${proposals} voorstellen staan bij Te controleren` : "Niets op te ruimen");
        } catch (error) {
          toast(
            error instanceof ApiError && error.status >= 400 && error.status < 600 ? error.message : "Opruimen kan alleen met verbinding.",
          );
        } finally {
          setBusy(false);
        }
      }}
    >
      <Sparkles aria-hidden className="size-5" />
      {busy ? "Bezig met opruimen" : "Nieuw opruimen"}
    </Button>
  );
}

/**
 * After the AI put something in place by itself: a bar with what it did and "Ongedaan maken" (B31). Once per item
 * per device; on the device that captured it and on the others alike.
 */
export function AiApplied() {
  const applied = useLiveQuery(async () => {
    const seen = new Set((await getMeta<string[]>("aiSeen")) ?? []);
    return (await db.tasks.toArray()).filter(
      (t) =>
        t.ai_state === "applied" &&
        t.ai_proposal &&
        !seen.has(t.id) &&
        // Done or Weg by PK himself since (L142): nothing to undo.
        !(t.ai_proposal.kind !== "appointment" && t.ai_proposal.kind !== "date" && (t.status === "done" || t.status === "cancelled")),
    );
  });
  const shown = useRef(new Set<string>());
  useEffect(() => {
    if (!applied?.length) return;
    const fresh = applied.filter((t) => !shown.current.has(t.id));
    if (!fresh.length) return;
    for (const t of fresh) {
      shown.current.add(t.id);
      // Only what arrived recently: an old one from another device is not news any more.
      if (Date.now() - Date.parse(t.updated_at) > 10 * 60 * 1000) continue;
      toast(describeUnderstood(t.ai_proposal!, displayDate), {
        duration: 10_000,
        action: { label: "Ongedaan maken", onClick: () => void undoApplied(t.id) },
      });
    }
    void (async () => {
      const seen = new Set((await getMeta<string[]>("aiSeen")) ?? []);
      for (const t of fresh) seen.add(t.id);
      await setMeta("aiSeen", [...seen].slice(-500));
    })();
  }, [applied]);
  return null;
}
